import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { Card, CardBody, Row, Col, Button } from 'reactstrap';
import { getPlanById, isTrialValid, getTrialDaysRemaining, getSpecialtyFields, getPlansForSpecialty } from 'config/subscriptionPlans';
import { cancelTrialAutoCharge, resumeTrialAutoCharge, billingActionReset } from 'redux/Billing/BillingActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';
import ChangeTrialPlanModal from './ChangeTrialPlanModal';

const BRAND_ICONS = {
    visa: 'fab fa-cc-visa',
    mastercard: 'fab fa-cc-mastercard',
    amex: 'fab fa-cc-amex',
    discover: 'fab fa-cc-discover',
    diners: 'fab fa-cc-diners-club',
    jcb: 'fab fa-cc-jcb',
};

const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TrialManagementCard = ({ userDetails, paymentMethods, activeSpecialty = 'pmr' }) => {
    const dispatch = useDispatch();
    const [cancelling, setCancelling] = useState(false);
    const [resuming, setResuming] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);

    if (!userDetails) return null;

    const fields = getSpecialtyFields(activeSpecialty);
    const isPending = userDetails[fields.autoChargeStatus] === 'pending';
    const isCancelled = userDetails[fields.autoChargeStatus] === 'cancelled';
    const trialActive = isTrialValid(userDetails[fields.trialStartDate]);
    // Stay mounted while cancelled so the user can undo — the card used to
    // disappear on cancel, leaving no way back short of buying manually.
    if ((!isPending && !isCancelled) || !trialActive) return null;

    const selectedPlanId = userDetails[fields.selectedPlanId];
    // Look up plan from specialty-specific plans first, fall back to base plans
    const specialtyPlans = getPlansForSpecialty(activeSpecialty);
    const plan = specialtyPlans.find(p => p.id === selectedPlanId) || getPlanById(selectedPlanId);
    const daysLeft = getTrialDaysRemaining(userDetails[fields.trialStartDate]);

    const trialStart = new Date(userDetails[fields.trialStartDate]);
    const chargeDate = new Date(trialStart);
    chargeDate.setDate(chargeDate.getDate() + 14);

    const cardToCharge = (paymentMethods || []).find(pm => pm.id === userDetails.stripePaymentMethodId)
        || (paymentMethods || []).find(pm => pm.isDefault);

    const urgent = daysLeft <= 3;
    const gradient = urgent ? 'bg-gradient-danger' : 'bg-gradient-warning';
    const color = urgent ? 'danger' : 'warning';

    const handleCancel = async () => {
        const res = await Swal.fire({
            title: 'Cancel auto-charge?',
            text: 'Your 14-day free trial will still run, but your card will not be charged when it ends. You can manually subscribe anytime.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel auto-charge',
            cancelButtonText: 'Keep auto-charge',
            confirmButtonColor: '#f5365c',
        });
        if (!res.isConfirmed) return;

        setCancelling(true);
        try {
            await dispatch(cancelTrialAutoCharge());
            await dispatch(getUserData({ onLogin: true }));
            dispatch(billingActionReset());
            Swal.fire({ title: 'Auto-Charge Cancelled', text: 'Your trial will still run through day 14. No charge will occur.', icon: 'success', timer: 2500 });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to cancel auto-charge', 'error');
        } finally {
            setCancelling(false);
        }
    };

    const handleResume = async () => {
        const res = await Swal.fire({
            title: 'Resume auto-charge?',
            html: plan
                ? `Your card will be charged <strong>$${plan.price}</strong> for ${plan.name} on ${formatDate(chargeDate)}, when your trial ends.`
                : `Your card will be charged when your trial ends on ${formatDate(chargeDate)}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, resume auto-charge',
            cancelButtonText: 'Keep it off',
            confirmButtonColor: '#2dce89',
        });
        if (!res.isConfirmed) return;

        setResuming(true);
        try {
            await dispatch(resumeTrialAutoCharge());
            await dispatch(getUserData({ onLogin: true }));
            dispatch(billingActionReset());
            Swal.fire({ title: 'Auto-Charge Resumed', text: `Your card will be charged on ${formatDate(chargeDate)}.`, icon: 'success', timer: 2500 });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to resume auto-charge', 'error');
        } finally {
            setResuming(false);
        }
    };

    const cardIconClass = cardToCharge ? (BRAND_ICONS[cardToCharge.brand] || 'far fa-credit-card') : 'far fa-credit-card';

    if (isCancelled) {
        return (
            <Card className="shadow border-0" style={{ overflow: 'hidden' }}>
                <div className="bg-gradient-info" style={{ height: '4px' }} />
                <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-3">
                        <div
                            className="icon icon-shape bg-gradient-info text-white rounded-circle shadow mr-3"
                            style={{ width: '64px', height: '64px', minWidth: '64px' }}
                        >
                            <i className="fas fa-ban" style={{ fontSize: '1.75rem' }} />
                        </div>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <h6
                                className="text-uppercase text-muted mb-1 font-weight-bold"
                                style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}
                            >
                                Auto-Charge Cancelled
                            </h6>
                            <h2 className="mb-1 text-info" style={{ lineHeight: 1.2 }}>
                                Trial ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                            </h2>
                            <p className="text-muted mb-0 small">
                                Your card will not be charged. Access ends {formatDate(chargeDate)} unless you subscribe.
                            </p>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                        <Button color="success" outline onClick={handleResume} disabled={resuming}>
                            {resuming
                                ? <><i className="fas fa-spinner fa-spin mr-1" /> Resuming...</>
                                : <><i className="fas fa-undo mr-1" /> Resume Auto-Charge</>}
                        </Button>
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            <Card className="shadow border-0" style={{ overflow: 'hidden' }}>
                <div className={gradient} style={{ height: '4px' }} />
                <CardBody className="p-4">
                    {/* Header row */}
                    <div className="d-flex align-items-center mb-4">
                        <div
                            className={`icon icon-shape ${gradient} text-white rounded-circle shadow mr-3`}
                            style={{ width: '64px', height: '64px', minWidth: '64px' }}
                        >
                            <i className="fas fa-hourglass-half" style={{ fontSize: '1.75rem' }} />
                        </div>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <h6
                                className="text-uppercase text-muted mb-1 font-weight-bold"
                                style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}
                            >
                                Trial Auto-Charge Scheduled
                            </h6>
                            <h2 className={`mb-1 text-${color}`} style={{ lineHeight: 1.2 }}>
                                Charging in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                            </h2>
                            <p className="text-muted mb-0 small">
                                {plan ? `$${plan.price} will be charged on ${formatDate(chargeDate)}` : `Charges on ${formatDate(chargeDate)}`}
                            </p>
                        </div>
                    </div>

                    {/* Info grid */}
                    <Row className="mb-4">
                        <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                            <div className="p-3 border rounded h-100">
                                <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                    <i className="fas fa-box mr-1" /> Plan
                                </small>
                                <span className="font-weight-bold">
                                    {plan ? plan.name : 'No plan selected'}
                                </span>
                            </div>
                        </Col>
                        <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                            <div className="p-3 border rounded h-100">
                                <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                    <i className="fas fa-dollar-sign mr-1" /> Amount
                                </small>
                                <span className="font-weight-bold text-success">
                                    {plan ? `$${plan.price}` : '—'}
                                </span>
                            </div>
                        </Col>
                        <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                            <div className="p-3 border rounded h-100">
                                <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                    <i className="fas fa-credit-card mr-1" /> Card
                                </small>
                                {cardToCharge ? (
                                    <div className="d-flex align-items-center">
                                        <i className={`${cardIconClass} mr-2`} style={{ fontSize: '1.25rem' }} />
                                        <span className="font-weight-bold">•••• {cardToCharge.last4}</span>
                                    </div>
                                ) : (
                                    <span className="font-weight-bold text-danger">No card</span>
                                )}
                            </div>
                        </Col>
                        <Col xs="6" lg="3">
                            <div className="p-3 border rounded h-100">
                                <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                    <i className="fas fa-calendar-alt mr-1" /> Charge Date
                                </small>
                                <span className="font-weight-bold">{formatDate(chargeDate)}</span>
                            </div>
                        </Col>
                    </Row>

                    {/* Actions */}
                    <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                        <Button color="primary" outline onClick={() => setPlanModalOpen(true)}>
                            <i className="fas fa-exchange-alt mr-1" /> Change Plan
                        </Button>
                        <Button color="danger" outline onClick={handleCancel} disabled={cancelling}>
                            {cancelling
                                ? <><i className="fas fa-spinner fa-spin mr-1" /> Cancelling...</>
                                : <><i className="fas fa-ban mr-1" /> Cancel Auto-Charge</>}
                        </Button>
                    </div>
                </CardBody>
            </Card>
            <ChangeTrialPlanModal
                isOpen={planModalOpen}
                toggle={() => setPlanModalOpen(v => !v)}
                currentPlanId={selectedPlanId}
                userDetails={userDetails}
                activeSpecialty={activeSpecialty}
            />
        </>
    );
};

const mapStateToProps = state => ({
    userDetails: state.loginAuth.userDetails,
    paymentMethods: state.paymentMethods.paymentMethods,
    activeSpecialty: state.specialty.activeSpecialty,
});

export default connect(mapStateToProps)(TrialManagementCard);
